import { useState } from 'react';
import { supabase } from '../supabaseClient';

const LABELS = ['A', 'B', 'C', 'D'];

// The quiz belongs to a module, so the parent (CoursePage) already knows which
// quiz to show and whether the user has attempted it. It passes both in, which
// keeps the course page to two extra requests no matter how many modules have a
// quiz. The questions are only fetched when the user actually opens the quiz.
const QuizSection = ({ quiz, userId, initialAttempt = null, onAttemptSaved }) => {
  const [open, setOpen] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(null);
  const [attempt, setAttempt] = useState(initialAttempt);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const ratio = Number(quiz.pass_ratio);
  const passPct = Math.round(ratio * 100);
  const passThreshold = Math.ceil(questions.length * ratio);
  const allAnswered = questions.length > 0 && Object.keys(answers).length === questions.length;

  const open_ = async () => {
    setOpen(true);
    setSubmitted(false);
    setAnswers({});
    setScore(null);
    setError('');
    if (questions.length) return;

    setLoading(true);
    const { data, error: qErr } = await supabase
      .from('quiz_questions')
      .select('*')
      .eq('quiz_id', quiz.id)
      .order('order_index');
    setLoading(false);

    if (qErr) {
      setError('A kérdéseket nem sikerült betölteni. Frissítsd az oldalt.');
      return;
    }
    setQuestions(data || []);
  };

  const submitQuiz = async () => {
    if (saving) return;
    setSaving(true);
    setError('');

    const correct = questions.filter(q => answers[q.id] === q.correct_index).length;
    const passed = correct >= Math.ceil(questions.length * ratio);

    const { error: insErr } = await supabase.from('quiz_attempts').insert({
      user_id: userId,
      quiz_id: quiz.id,
      score: correct,
      total: questions.length,
      passed,
    });

    if (insErr) {
      setSaving(false);
      setError('Az eredményt nem sikerült menteni. Próbáld újra.');
      return;
    }

    const { data: latest } = await supabase
      .from('quiz_attempts')
      .select('*')
      .eq('quiz_id', quiz.id)
      .eq('user_id', userId)
      .order('completed_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (latest) {
      setAttempt(latest);
      if (onAttemptSaved) onAttemptSaved(quiz.id, latest);
    }

    setScore(correct);
    setSubmitted(true);
    setSaving(false);
  };

  const shell = {
    background: 'rgba(0,210,200,0.04)',
    border: '1px solid rgba(0,210,200,0.15)',
    borderRadius: 12,
    padding: '14px 16px',
    marginTop: 10,
  };

  // Collapsed: title, last result, and a button to start.
  if (!open) {
    return (
      <div style={{ ...shell, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 18 }}>📝</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{quiz.title}</div>
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginTop: 2 }}>
            {attempt
              ? `Előző eredményed: ${attempt.score}/${attempt.total} · ${attempt.passed ? '✅ teljesítve' : '❌ nem sikerült'}`
              : `Modul kvíz · ${passPct}% kell a teljesítéshez`}
          </div>
        </div>
        <button
          onClick={open_}
          style={{
            background: attempt ? 'rgba(255,255,255,0.08)' : '#00D2C8',
            color: attempt ? 'rgba(255,255,255,0.75)' : '#07070b',
            border: attempt ? '1px solid rgba(255,255,255,0.12)' : 'none',
            borderRadius: 8, padding: '8px 16px',
            fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          {attempt ? 'Újrapróbálom' : 'Kvíz indítása →'}
        </button>
      </div>
    );
  }

  return (
    <div style={shell}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
        <h3 style={{ color: '#fff', margin: 0, fontSize: 15 }}>📝 {quiz.title}</h3>
        <button
          onClick={() => setOpen(false)}
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'rgba(255,255,255,0.4)', fontSize: 18, lineHeight: 1, padding: 4,
          }}
          aria-label="Bezárás"
        >
          ×
        </button>
      </div>

      {error && (
        <div style={{
          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 8, padding: '10px 14px', marginBottom: 14,
          fontSize: 13, color: '#fca5a5',
        }}>
          {error}
        </div>
      )}

      {loading && (
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, margin: 0 }}>Kérdések betöltése...</p>
      )}

      {!loading && !submitted && questions.length > 0 && (
        <>
          {questions.map((q, idx) => (
            <div key={q.id} style={{ marginBottom: 20 }}>
              <p style={{ color: '#fff', fontWeight: 600, margin: '0 0 10px', fontSize: 14 }}>
                {idx + 1}. {q.question}
              </p>
              {q.options.map((opt, optIdx) => {
                const active = answers[q.id] === optIdx;
                return (
                  <div
                    key={optIdx}
                    onClick={() => setAnswers({ ...answers, [q.id]: optIdx })}
                    style={{
                      padding: '9px 13px', borderRadius: 8,
                      cursor: 'pointer', marginBottom: 6,
                      border: active ? '1px solid #00D2C8' : '1px solid rgba(255,255,255,0.1)',
                      background: active ? 'rgba(0,210,200,0.1)' : 'rgba(255,255,255,0.03)',
                      color: active ? '#00D2C8' : 'rgba(255,255,255,0.7)',
                      fontSize: 13, transition: 'all 0.15s',
                    }}
                  >
                    {LABELS[optIdx]}) {opt}
                  </div>
                );
              })}
            </div>
          ))}

          <button
            onClick={submitQuiz}
            disabled={!allAnswered || saving}
            style={{
              background: allAnswered && !saving ? '#00D2C8' : 'rgba(255,255,255,0.1)',
              color: allAnswered && !saving ? '#07070b' : 'rgba(255,255,255,0.3)',
              border: 'none', borderRadius: 8,
              padding: '11px 24px', fontWeight: 700,
              fontSize: 14, cursor: allAnswered && !saving ? 'pointer' : 'default', width: '100%',
            }}
          >
            {saving ? 'Mentés...' : 'Beküldöm a válaszaimat'}
          </button>
        </>
      )}

      {submitted && (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>
            {score >= passThreshold ? '🎉' : '😅'}
          </div>
          <h3 style={{ color: '#fff', margin: '0 0 6px', fontSize: 16 }}>
            {score}/{questions.length} helyes válasz
          </h3>
          <p style={{ color: 'rgba(255,255,255,0.5)', margin: '0 0 18px', fontSize: 13 }}>
            {score >= passThreshold
              ? 'Gratulálok! Teljesítetted a kvízt.'
              : `Próbáld újra — legalább ${passPct}% kell a teljesítéshez.`}
          </p>

          {questions.map(q => {
            const ok = answers[q.id] === q.correct_index;
            return (
              <div key={q.id} style={{
                textAlign: 'left', marginBottom: 10,
                padding: '10px 13px', borderRadius: 8,
                background: ok ? 'rgba(0,210,200,0.08)' : 'rgba(239,68,68,0.08)',
                border: `1px solid ${ok ? 'rgba(0,210,200,0.2)' : 'rgba(239,68,68,0.2)'}`,
              }}>
                <p style={{ color: '#fff', margin: '0 0 4px', fontSize: 13, fontWeight: 600 }}>
                  {ok ? '✓' : '✗'} {q.question}
                </p>
                <p style={{ color: 'rgba(255,255,255,0.5)', margin: 0, fontSize: 12 }}>
                  Helyes: {q.options[q.correct_index]}
                </p>
                {q.explanation && (
                  <p style={{ color: 'rgba(0,210,200,0.8)', margin: '4px 0 0', fontSize: 12 }}>
                    💡 {q.explanation}
                  </p>
                )}
              </div>
            );
          })}

          <button
            onClick={open_}
            style={{
              background: 'rgba(255,255,255,0.08)',
              color: 'rgba(255,255,255,0.7)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8, padding: '9px 20px',
              cursor: 'pointer', fontSize: 13, marginTop: 4,
            }}
          >
            Újrapróbálom
          </button>
        </div>
      )}
    </div>
  );
};

export default QuizSection;
